import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { isAxiosError } from 'axios';
import { ModalDialog, Dialog } from '../dialog/index.js';
import { ExerciseIframe } from './exercise-iframe.js';
import { stopAssignmentSession, stopFreeExerciseSession, startAssignmentSession, resumeAssignmentSession, startFreeExerciseSession, resumeFreeExerciseSession, pauseAssignmentSession, pauseFreeExerciseSession } from './util/exercises-api.js';
import { BearerTokenGetterContext } from '../../context/BearerTokenGetterContext.js';
import { ExerciseLoading } from './states/exercise-loading.js';
import { MediaLinkDialogHeader } from '../media-link-dialog-header/index.js';
import { ExerciseResult } from './states/exercise-result.js';
import { Button } from '../button/index.js';
import { ExercisePauseDialog } from './components/dialogs/exercise-pause-dialog.js';
import { ExerciseTranslationsContext } from './contexts/ExerciseTranslationsContext.js';
import { ExercisePaused } from './states/exercise-paused.js';
import { ExerciseFailed } from './states/exercise-failed.js';
import { ConfirmedAction } from '../confirmed-action/index.js';
import { AssignmentExpiredDialog } from './components/dialogs/assignment-expired-dialog.js';
import img from '../../assets/images/p_logo.svg.js';
import { createPortal } from 'react-dom';
import styles from './exercise-dialog.module.css.js';
import useAnalytics from '../../hooks/useAnalytics.js';
import { ANALYTICS_EVENT_TYPES } from '../../hooks/constants/Analytics.js';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const ExerciseDialog = ({ initialPhase, medialink, language, onReview, onClose, superModuleHasExerciseEntrance, onSessionPaused, onSessionFinished, onAssignmentExpired, assignmentId, analyticsData, studioGroup, }) => {
    const [phase, setPhase] = useState(initialPhase);
    const exerciseTranslations = useContext(ExerciseTranslationsContext);
    const [url, setUrl] = useState();
    const [flowState, setFlowState] = useState('loading');
    const [retryAction, setRetryAction] = useState();
    const [expiredError, setExpiredError] = useState();
    const [pauseSessionResponse, setPauseSessionResponse] = useState();
    const [stopSessionResponse, setStopSessionResponse] = useState();
    const { id: medialinkId, moduleId } = medialink;
    const getBearerToken = useContext(BearerTokenGetterContext);
    const [serviceAvailable, setServiceAvailable] = useState(true);
    const eventCaptured = useRef(false);
    const analytics = useAnalytics();
    useEffect(() => {
        setPhase(initialPhase);
        setUrl(undefined);
        setPauseSessionResponse(undefined);
        setStopSessionResponse(undefined);
        setRetryAction(undefined);
        setFlowState('loading');
        setServiceAvailable(true);
    }, [initialPhase]);
    useEffect(() => {
        const run = async () => {
            const token = await getBearerToken();
            let exerciseUrl;
            if (assignmentId) {
                try {
                    if (phase === 'start') {
                        exerciseUrl = await startAssignmentSession(token, moduleId, assignmentId, language, studioGroup);
                    }
                    if (phase === 'resume') {
                        exerciseUrl = await resumeAssignmentSession(token, moduleId, assignmentId, studioGroup);
                    }
                }
                catch (e) {
                    if (isAxiosError(e) && e.response && e.response.status === 410) {
                        setExpiredError(true);
                        if (onAssignmentExpired)
                            onAssignmentExpired();
                    }
                    else
                        throw e;
                }
            }
            else {
                if (phase === 'start') {
                    exerciseUrl = await startFreeExerciseSession(token, moduleId, medialinkId, language, studioGroup);
                }
                if (phase === 'resume') {
                    exerciseUrl = await resumeFreeExerciseSession(token, moduleId, medialinkId, studioGroup);
                }
            }
            if (exerciseUrl)
                setUrl(exerciseUrl);
            setFlowState('play');
        };
        if (flowState === 'loading')
            run().catch(() => setServiceAvailable(false));
    }, [
        assignmentId,
        flowState,
        getBearerToken,
        language,
        medialinkId,
        moduleId,
        onAssignmentExpired,
        phase,
        studioGroup,
    ]);
    useEffect(() => {
        const captureEvent = async () => {
            const token = await getBearerToken();
            if (analyticsData && token && !eventCaptured.current) {
                eventCaptured.current = true;
                analytics.capture({
                    eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
                    objectId: medialinkId,
                    analyticsData,
                }, token);
            }
        };
        captureEvent();
    }, [analytics, analyticsData, getBearerToken, medialinkId]);
    const handleClose = useCallback(() => {
        setExpiredError(false);
        onClose();
    }, [onClose]);
    const onPause = async () => {
        setRetryAction(undefined);
        setFlowState('calculating');
        try {
            const token = await getBearerToken();
            const data = assignmentId
                ? await pauseAssignmentSession(token, moduleId, assignmentId, studioGroup)
                : await pauseFreeExerciseSession(token, moduleId, medialinkId, studioGroup);
            setPauseSessionResponse(data);
            setFlowState('paused');
            if (onSessionPaused)
                onSessionPaused(data.result);
        }
        catch (e) {
            setRetryAction(() => onPause);
        }
    };
    const onExerciseFinished = useCallback(async () => {
        setRetryAction(undefined);
        setFlowState('calculating');
        try {
            const token = await getBearerToken();
            const data = assignmentId
                ? await stopAssignmentSession(token, moduleId, assignmentId, studioGroup)
                : await stopFreeExerciseSession(token, moduleId, medialinkId, studioGroup);
            setStopSessionResponse(data);
            setFlowState('stopped');
            if (onSessionFinished)
                onSessionFinished(data.result);
        }
        catch (e) {
            setRetryAction(() => onExerciseFinished);
        }
    }, [getBearerToken, assignmentId, moduleId, medialinkId, studioGroup, onSessionFinished]);
    const onRestart = () => {
        setPhase('start');
        setUrl(undefined);
        setPauseSessionResponse(undefined);
        setStopSessionResponse(undefined);
        setRetryAction(undefined);
        setFlowState('loading');
    };
    const { closeDialog, generalNode, exerciseDialog: translations } = exerciseTranslations;
    return expiredError ? (React.createElement(AssignmentExpiredDialog, { onClose: handleClose })) : (React.createElement(ModalDialog, { id: 'bc-exercise-dialog', closeTitle: closeDialog, onClose: handleClose, fullScreen: true, showCloseButton: !serviceAvailable, closeOnClickOutside: false, className: styles.dialog },
        React.createElement(MediaLinkDialogHeader, { mediaLink: medialink, superModuleHasExerciseEntrance: superModuleHasExerciseEntrance, translations: {
                generalNode,
                close: closeDialog,
            }, endAdornment: flowState === 'play' &&
                serviceAvailable && (React.createElement(ConfirmedAction, { onConfirm: onPause, trigger: (showConfirmation, dialogVisible) => (React.createElement(Button, { variant: 'primary', onClick: showConfirmation, disabled: dialogVisible, className: styles.pauseButton },
                    React.createElement("i", { className: 'bc-icon bc-icon-pause' }),
                    translations.buttons.pause)), confirmation: (confirm, cancel) => createPortal(React.createElement(ExercisePauseDialog, { onClose: cancel, onPause: confirm }), document.body) })), studioGroup: studioGroup }),
        React.createElement(Dialog.Body, null, retryAction ? (React.createElement(ExerciseFailed, { onClose: handleClose, onRetry: retryAction })) : (React.createElement(React.Fragment, null, !serviceAvailable ? (React.createElement("div", { className: styles['service-not-available'] },
            React.createElement("img", { src: img, alt: 'P logo', width: '180', height: '180' }),
            React.createElement("h1", { className: 'heading' }, exerciseTranslations.serviceNotAvailable.title),
            React.createElement("p", null, exerciseTranslations.serviceNotAvailable.p1),
            React.createElement("p", null, exerciseTranslations.serviceNotAvailable.p2))) : (React.createElement(React.Fragment, null,
            flowState === 'loading' && React.createElement(ExerciseLoading, null, exerciseTranslations.loading),
            flowState === 'calculating' && React.createElement(ExerciseLoading, null, translations.calculating),
            flowState === 'paused' && pauseSessionResponse && (React.createElement(ExercisePaused, { result: pauseSessionResponse.result, onClose: handleClose })),
            flowState === 'stopped' && stopSessionResponse && (React.createElement(ExerciseResult, { result: stopSessionResponse.result, onRestart: stopSessionResponse.notRestartable ? undefined : onRestart, onReview: onReview, onClose: handleClose })),
            flowState === 'play' && url && (React.createElement(ExerciseIframe, { exerciseUrl: url, medialink: medialink, onExerciseFinished: onExerciseFinished })))))))));
};

export { ExerciseDialog };
